import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navLinks, site } from '@/mocks/site';

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const overlay = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/collections?q=${encodeURIComponent(q)}` : '/collections');
    setSearchOpen(false);
  };

  const linkBase = overlay
    ? 'text-background-100 hover:text-accent-300'
    : 'text-foreground-700 hover:text-primary-600';
  const activeCls = overlay ? 'text-accent-300' : 'text-primary-600';

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 ${
        overlay
          ? 'bg-transparent border-b border-transparent'
          : 'bg-background-50/95 backdrop-blur border-b border-background-200'
      }`}
    >
      <div className="flex h-16 items-center justify-between px-4 md:px-8">
        <Link to="/" className="flex items-center gap-3 group">
          <span
            className={`w-10 h-10 flex items-center justify-center rounded-md ${
              overlay ? 'bg-primary-500 text-background-50' : 'bg-primary-500 text-background-50'
            }`}
          >
            <i className="ri-article-line text-xl"></i>
          </span>
          <span className="leading-tight">
            <strong
              className={`block font-heading text-lg font-bold tracking-wide ${
                overlay ? 'text-background-50' : 'text-foreground-950'
              }`}
            >
              {site.name}
            </strong>
            <span
              className={`block text-[11px] tracking-widest ${
                overlay ? 'text-background-200' : 'text-foreground-500'
              }`}
            >
              {site.slogan}
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden md:flex items-center gap-8">
          {navLinks.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`text-sm font-medium whitespace-nowrap transition-colors ${
                  active ? activeCls : linkBase
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className={`hidden lg:flex items-center rounded-full border px-3 py-1.5 transition-all ${
              overlay
                ? 'border-background-100/40 bg-background-50/10'
                : 'border-background-200 bg-background-100'
            } ${searchOpen ? 'w-56' : 'w-40'}`}
          >
            <i
              className={`ri-search-line text-sm ${
                overlay ? 'text-background-200' : 'text-foreground-400'
              }`}
            ></i>
            <input
              type="search"
              value={keyword}
              onFocus={() => setSearchOpen(true)}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索爆料关键词"
              aria-label="站内搜索"
              className={`ml-2 w-full bg-transparent text-sm outline-none ${
                overlay
                  ? 'text-background-50 placeholder:text-background-200/70'
                  : 'text-foreground-900 placeholder:text-foreground-400'
              }`}
            />
          </form>

          <Link
            to="/collections"
            className="hidden md:inline-flex items-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
          >
            进入合集
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md ${
              overlay ? 'text-background-50' : 'text-foreground-900'
            }`}
          >
            <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <nav aria-label="移动端导航" className="flex flex-col gap-1">
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-3 text-sm font-medium ${
                  location.pathname === item.to
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-800 hover:bg-background-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <form onSubmit={submitSearch} className="mt-3 flex items-center rounded-md border border-background-200 bg-background-100 px-3 py-2">
            <i className="ri-search-line text-sm text-foreground-400"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索爆料关键词"
              aria-label="站内搜索"
              className="ml-2 w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
            />
          </form>
        </div>
      )}
    </header>
  );
}